import React from 'react'
import { Box, Skeleton, Stack, Paper, Grid } from '@mui/material'

// Skeleton loader for package cards
export const PackageCardSkeleton: React.FC = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2.48 },
        bgcolor: '#121417',
        borderRadius: { xs: 6, md: 12 },
        height: '100%',
        width: { xs: '85%', sm: '100%', md: '95%' },
        maxWidth: { xs: '340px', sm: 'none' },
        mx: { xs: 'auto', md: 'auto' },
        border: '1px solid #2A2E33',
      }}
    >
      <Stack spacing={{ xs: 1.2, md: 2.07 }}>
        <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width="40%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width="50%" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width="70%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Box sx={{ width: '100%', height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1.5 }} />
        <Stack spacing={1}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="text" width="80%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          ))}
        </Stack>
        <Skeleton variant="rectangular" height={45} sx={{ borderRadius: { xs: 6, md: 12 }, bgcolor: 'rgba(255,255,255,0.1)', mt: 'auto' }} />
      </Stack>
    </Paper>
  )
}

// Skeleton loader for FAQ items
export const FAQSkeleton: React.FC = () => {
  return (
    <Stack spacing={1}>
      {[1, 2, 3, 4].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={60}
          sx={{
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.05)',
          }}
        />
      ))}
    </Stack>
  )
}

// Skeleton loader for contact details
export const ContactDetailsSkeleton: React.FC = () => {
  return (
    <Stack spacing={1}>
      {[1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 1,
            borderRadius: 2,
          }}
        >
          <Skeleton
            variant="rectangular"
            width={44}
            height={44}
            sx={{ borderRadius: 2, bgcolor: 'rgba(0,0,0,0.05)' }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={16} sx={{ mb: 0.5, bgcolor: 'rgba(0,0,0,0.05)' }} />
            <Skeleton variant="text" width="70%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

// Skeleton loader for about page cards
export const AboutCardsSkeleton: React.FC = () => {
  return (
    <Box>
      {/* Small cards skeleton */}
      <Box sx={{ mb: 8 }}>
        <Skeleton variant="text" width="30%" height={18} sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width="60%" height={48} sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Skeleton variant="text" width="80%" height={24} sx={{ mb: 5, bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Grid container spacing={4}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 4.5,
                  bgcolor: 'white',
                  borderRadius: 3,
                  height: '100%',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <Stack spacing={3}>
                  <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: 2, bgcolor: 'rgba(0,0,0,0.05)' }} />
                  <Skeleton variant="text" width="70%" height={32} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
                  <Skeleton variant="text" width="90%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* Big card skeleton */}
      <Paper
        elevation={0}
        sx={{
          p: 6,
          bgcolor: 'white',
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="text" width="60%" height={56} sx={{ mx: 'auto', mb: 1, bgcolor: 'rgba(0,0,0,0.05)' }} />
              <Skeleton variant="text" width="80%" height={20} sx={{ mx: 'auto', bgcolor: 'rgba(0,0,0,0.05)' }} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ pt: 3, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
          <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2, bgcolor: 'rgba(0,0,0,0.05)' }} />
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1, bgcolor: 'rgba(0,0,0,0.05)' }} />
          <Skeleton variant="text" width="95%" height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
        </Box>
      </Paper>
    </Box>
  )
}

// Skeleton loader for legal page
export const LegalPageSkeleton: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      {/* Sidebar skeleton */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', md: '300px' },
          height: 'fit-content',
          borderRadius: 3,
          bgcolor: 'white',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Stack spacing={0}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ p: 2.5, borderBottom: i < 3 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
              <Skeleton variant="text" width="80%" height={24} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
            </Box>
          ))}
        </Stack>
      </Paper>
      {/* Content skeleton */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 3,
          p: 4,
          bgcolor: 'white',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2, bgcolor: 'rgba(0,0,0,0.05)' }} />
        <Skeleton variant="rectangular" width="40%" height={32} sx={{ mb: 4, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.05)' }} />
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="text" width={i % 2 === 0 ? '90%' : '100%'} height={20} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} />
          ))}
        </Stack>
      </Paper>
    </Box>
  )
}

